import { memo, useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CS } from 'back-end-api'

// Sentinel filter values (outside the RegistrationStatus enum range) for
// summary labels that are not real RegistrationStatus values.
export const DECLARED_ILL_FILTER = 1001
export const DECLARED_NOT_ILL_FILTER = 1002

const ALL_STATUSES: { value: number; key: string }[] = [
  { value: CS.RegistrationStatus.SummaryInProgress, key: 'SummaryInProgress' },
  { value: CS.RegistrationStatus.SummarySubmitted, key: 'SummarySubmitted' },
  { value: CS.RegistrationStatus.SummaryAdviceSent, key: 'SummaryAdviceSent' },
  { value: CS.RegistrationStatus.SummaryReferralSent, key: 'SummaryReferralSent' },
  { value: CS.RegistrationStatus.SummaryReferralSpecialSent, key: 'SummaryReferralSpecialSent' },
  { value: CS.RegistrationStatus.AppointmentPending, key: 'AppointmentPending' },
  { value: CS.RegistrationStatus.AppointmentAccepted, key: 'AppointmentAccepted' },
  { value: CS.RegistrationStatus.AppointmentReady, key: 'AppointmentReady' },
  { value: CS.RegistrationStatus.AppointmentInProgress, key: 'AppointmentInProgress' },
  { value: CS.RegistrationStatus.AppointmentDone, key: 'AppointmentDone' },
  { value: CS.RegistrationStatus.SummaryConsulted, key: 'SummaryConsulted' },
  { value: CS.RegistrationStatus.AppointmentDeclined, key: 'AppointmentDeclined' },
  { value: CS.RegistrationStatus.AppointmentCancelled, key: 'AppointmentCancelled' },
  { value: CS.RegistrationStatus.AppointmentTimeout, key: 'AppointmentTimeout' },
  { value: CS.RegistrationStatus.AppointmentMissed, key: 'AppointmentMissed' },
  { value: CS.RegistrationStatus.SummaryCancelled, key: 'SummaryCancelled' },
  { value: CS.RegistrationStatus.SummaryTimeout, key: 'SummaryTimeout' },
  // Ill / Not-ill (declared) filter options disabled
]

interface StatusFilterProps {
  label: string
  clearLabel: string
  tStatus: (key: string) => string
  activeFilters: Set<number>
  onToggle: (status: number) => void
  onClear: () => void
}

const StatusFilter = ({ label, clearLabel, tStatus, activeFilters, onToggle, onClear }: StatusFilterProps) => {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)

  const getPos = useCallback(() => {
    if (!btnRef.current) return { top: 0, left: 0 }
    const rect = btnRef.current.getBoundingClientRect()
    return { top: rect.bottom + 6, left: rect.left }
  }, [])

  const hasFilters = activeFilters.size > 0

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          backgroundColor: hasFilters ? '#2e4ce6' : '#fff',
          color: hasFilters ? '#fff' : '#2e4ce6',
          border: '1.5px solid #2e4ce6',
          borderRadius: '20px',
          padding: '4px 14px',
          fontSize: '0.8125rem',
          fontFamily: 'coresanscr65, sans-serif',
          cursor: 'pointer',
          transition: 'all 150ms ease',
          whiteSpace: 'nowrap',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
        {label}
        {hasFilters && (
          <span style={{
            backgroundColor: '#fff',
            color: '#2e4ce6',
            borderRadius: '50%',
            width: '18px',
            height: '18px',
            fontSize: '0.7rem',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'coresanscr65, sans-serif',
          }}>
            {activeFilters.size}
          </span>
        )}
      </button>
      {open && createPortal(
        <>
          <div
            role="presentation"
            onClick={() => setOpen(false)}
            onKeyDown={() => setOpen(false)}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
          />
          <div style={{
            position: 'fixed',
            top: getPos().top,
            left: getPos().left,
            backgroundColor: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 100,
            minWidth: '220px',
            maxHeight: '340px',
            overflowY: 'auto',
            padding: '6px 0',
            fontFamily: 'coresanscr45, sans-serif',
          }}>
            {hasFilters && (
              <div
                role="presentation"
                onClick={() => { onClear(); setOpen(false) }}
                onKeyDown={() => { onClear(); setOpen(false) }}
                style={{
                  padding: '8px 14px',
                  fontSize: '0.78rem',
                  color: '#2e4ce6',
                  cursor: 'pointer',
                  borderBottom: '1px solid #F3F4F6',
                  textAlign: 'center',
                  fontFamily: 'coresanscr65, sans-serif',
                }}
              >
                {clearLabel}
              </div>
            )}
            {ALL_STATUSES.map(s => {
              const checked = activeFilters.has(s.value)
              const checkId = `sf-${s.value}`
              return (
                <label
                  key={s.value}
                  htmlFor={checkId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '7px 14px',
                    cursor: 'pointer',
                    fontSize: '0.8125rem',
                    backgroundColor: checked ? '#EEF2FF' : 'transparent',
                    transition: 'background-color 100ms ease',
                  }}
                >
                  <input
                    id={checkId}
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(s.value)}
                    style={{ accentColor: '#2e4ce6', width: '15px', height: '15px' }}
                  />
                  <span style={{ color: '#202336' }}>{tStatus(s.key)}</span>
                </label>
              )
            })}
          </div>
        </>,
        document.body
      )}
    </>
  )
}

export default memo(StatusFilter)
